/**
 * The feature list — ONE file (threads/teach-features-in-app/, #1838).
 *
 * Every feature that teaches itself has one entry here:
 *   - `intro`      a one-time card the first time a person meets the feature:
 *                  three pictures, one line under each, Got it.
 *   - `tip`        a gold dot on a button the person has never used (office
 *                  only, never on the tech iPad). Mark the button with
 *                  `data-teach="<key>"` and the dot finds it.
 *   - `spotlight`  stronger than a dot: the screen dims and the button lights
 *                  up with one card (Help Desk + Tasks only).
 *
 * The rule for every future PR: add a button or a screen, add its entry here.
 * `features.keys.test.ts` fails CI if a `data-teach` key in the code has no
 * entry, or a picture is missing.
 *
 * Words are labels, not explanations (visual-first, 7th grade): one short line
 * per picture, no paragraphs.
 */

export type TeachRole = "tech" | "office";

/** Pictures live in pictures.tsx; this file stays pure so it tests in node. */
export type PicId =
    | "njlo1"
    | "njlo2"
    | "njlo3"
    | "lo1"
    | "lo2"
    | "lo3"
    | "jpGlance"
    | "jpMove"
    | "jpCmdk"
    | "jpPins"
    | "pb1"
    | "pb2"
    | "pb3"
    | "pbo1"
    | "pbo2"
    | "pbo3"
    | "grid1"
    | "grid2"
    | "grid3"
    | "gridFit"
    | "gridViews"
    | "gridWeek"
    | "mail3"
    | "chip1"
    | "chip2"
    | "chip3"
    | "list1"
    | "list2"
    | "wit1"
    | "wit2"
    | "wit3"
    | "quiet1"
    | "quiet2"
    | "quiet3"
    | "helpdesk"
    | "helpdeskTech"
    | "tasks"
    | "notes1"
    | "notes2"
    | "notes3"
    | "fa1"
    | "fa2"
    | "fa3"
    | "fao1"
    | "fao2"
    | "fao3";

export interface IntroStep {
    pic: PicId;
    line: string;
}

export interface SpotlightCopy {
    headline: string;
    line: string;
    pic: PicId;
}

export interface TeachFeature {
    key: string;
    /** Short name: the tip's title and the admin table's column. */
    name: string;
    /** Who it's for. A card or dot never shows to anyone else. */
    roles: TeachRole[];
    /** When it shipped (YYYY-MM-DD) — newer tips sort first among equals. */
    shipped: string;
    intro?: { title: string; steps: [IntroStep, IntroStep, IntroStep] };
    /**
     * Office only. `order` is the role order: lower shows first. Try it
     * presses the marked button, or opens ⌘K (`tryIt: "palette"`) when the
     * marked thing is a label, not a button.
     */
    tip?: { line: string; pic: PicId; order: number; tryIt?: "palette" };
    /** Help Desk + Tasks. Office copy, with an optional tech version. */
    spotlight?: { order: number; office: SpotlightCopy; tech?: SpotlightCopy };
    /** How a real use is counted, in plain words (docs + the admin table). */
    usedWhen: string;
    /**
     * Clicking the marked button counts as using it (default). False where
     * opening isn't using: the Help Desk counts a question SENT, Tasks a task
     * SENT — those come from the server (teachUsage.ts / noteFeatureUse).
     */
    clickIsUse?: boolean;
    /** A column in Settings → Team → Feature use. */
    adminColumn?: boolean;
}

export const FEATURES: TeachFeature[] = [
    // ── Featured first: the spotlight ────────────────────────────────────
    {
        key: "helpdesk",
        name: "Help Desk",
        roles: ["office", "tech"],
        shipped: "2026-10-01",
        spotlight: {
            order: 1,
            office: {
                headline: "Stuck? Ask here. It fixes things.",
                line: "Type what's wrong in plain words. If it can't fix it, Corbin gets it ready to fix.",
                pic: "helpdesk",
            },
            tech: {
                headline: "Something wrong? Tap here.",
                line: "Pick what happened. One tap tells the office. No typing.",
                pic: "helpdeskTech",
            },
        },
        tip: { line: "Ask why a job is stuck. It can fix most of it for you.", pic: "helpdesk", order: 1 },
        usedWhen: "A Help Desk question sent, a flag sent, or a tech's Flag a problem.",
        clickIsUse: false,
        adminColumn: true,
    },
    {
        key: "tasks",
        name: "Tasks",
        roles: ["office"],
        shipped: "2026-09-25",
        spotlight: {
            order: 2,
            office: {
                headline: "Send a task. See when it's done.",
                line: "A finished task shows a green check. Nothing to press. Nothing gets lost in a text.",
                pic: "tasks",
            },
        },
        tip: { line: "See what you sent and who finished it.", pic: "tasks", order: 2 },
        usedWhen: "A task sent.",
        clickIsUse: false,
        adminColumn: true,
    },

    // ── Intro cards ──────────────────────────────────────────────────────
    {
        key: "left-open.ipad",
        name: "Leave answer (iPad)",
        roles: ["tech"],
        shipped: "2026-10-08",
        intro: {
            // Approved mockup A3 (TwMWRHEyVGpWnd2an3pVPv, 10/10).
            title: "Leaving a job? One question first.",
            steps: [
                { pic: "njlo1", line: "Before you drive off, tell us about the job." },
                { pic: "njlo2", line: "Tap Done, Coming back, or Can't finish." },
                { pic: "njlo3", line: "Your next job waits until you answer." },
            ],
        },
        usedWhen: "He answered the leave question once.",
        adminColumn: true,
    },
    {
        key: "left-open.office",
        name: "Left open list",
        roles: ["office"],
        shipped: "2026-10-08",
        intro: {
            title: "Left open: jobs a tech walked away from",
            steps: [
                { pic: "lo1", line: "A new Left open count on the Grid." },
                { pic: "lo2", line: "Each job shows what the tech answered." },
                { pic: "lo3", line: "Orange means no answer yet. That one is yours to call." },
            ],
        },
        usedWhen: "Opened the Left open list.",
        adminColumn: true,
    },
    {
        key: "job-page.new",
        name: "New job page",
        roles: ["office"],
        shipped: "2026-10-09",
        intro: {
            title: "The new job page",
            steps: [
                { pic: "jpGlance", line: "The dark top shows the whole job at a glance." },
                { pic: "jpMove", line: "Move to ▾ puts the job in its next stage." },
                { pic: "jpCmdk", line: "⌘K does everything else. Pin what you use most." },
            ],
        },
        usedWhen: "Did anything on the new job page (audit log screen tag).",
        adminColumn: true,
    },
    {
        key: "point-board",
        name: "Point Board",
        roles: ["office"],
        shipped: "2026-10-08",
        intro: {
            title: "The Point Board",
            steps: [
                { pic: "pbo1", line: "Each tech's points for the pay period, against the goal." },
                { pic: "pbo2", line: "Waiting on report: test done, city paper not on the job yet." },
                { pic: "pbo3", line: "Report goes on the job, points move to Approved." },
            ],
        },
        usedWhen: "Opened the Point Board.",
        adminColumn: true,
    },
    {
        key: "points.tech",
        name: "Points (iPad)",
        roles: ["tech"],
        shipped: "2026-10-08",
        intro: {
            title: "Your points",
            steps: [
                { pic: "pb1", line: "Your points for this pay period, on one board." },
                { pic: "pb2", line: "Waiting on report: test done, city paper not on the job yet." },
                { pic: "pb3", line: "Report goes on the job, points move to Approved." },
            ],
        },
        usedWhen: "Opened My points.",
        adminColumn: true,
    },
    {
        key: "grid.new",
        name: "New Grid",
        roles: ["office"],
        shipped: "2026-10-07",
        intro: {
            title: "The new Grid",
            steps: [
                { pic: "grid1", line: "Card color is the job's state." },
                { pic: "grid2", line: "Ready check shows before you drag." },
                { pic: "grid3", line: "Hover or right-click a card for the rest." },
            ],
        },
        usedWhen: "Opened the Grid with the new Grid on.",
        adminColumn: true,
    },
    // The friendly Mailroom (10/9, #1847–#1851). ONE card on the Mailroom
    // (Corbin 10/10: "at a glance" + the list merged; no bells card — alerts
    // live on the item, not a bell). The Inbox chip and What is this? cards
    // show elsewhere.
    {
        key: "mailroom.new",
        name: "New Mailroom",
        roles: ["office"],
        shipped: "2026-10-09",
        intro: {
            title: "Your Mailroom, at a glance",
            steps: [
                { pic: "list1", line: "Needs me first. Team has the rest." },
                { pic: "list2", line: "Color = what. Face = whose. Bar = how long." },
                { pic: "mail3", line: "One big button does the next step." },
            ],
        },
        usedWhen: "Opened the Mailroom with the new view on.",
        adminColumn: true,
    },
    {
        key: "mailroom.chip",
        name: "Inbox button",
        roles: ["office"],
        shipped: "2026-10-09",
        intro: {
            title: "One number for your email",
            steps: [
                { pic: "chip1", line: "Gray: nothing needs you." },
                { pic: "chip2", line: "Orange: that many emails need you." },
                { pic: "chip3", line: "Hover or tap. Act without leaving." },
            ],
        },
        usedWhen: "Opened the Inbox preview.",
    },
    {
        key: "mailroom.what-is-this",
        name: "What is this?",
        roles: ["office"],
        shipped: "2026-10-09",
        intro: {
            title: "Every email answers: what is this?",
            steps: [
                { pic: "wit1", line: "What it is, in plain words." },
                { pic: "wit2", line: "Green sure. Orange check: look first." },
                { pic: "wit3", line: "One button does the next step." },
            ],
        },
        usedWhen: "Pressed the big button in the reader.",
    },

    {
        // The Quiet Toolbar (10/10): shows once, the first time someone turns
        // on "Try the quiet toolbar". Office and tech (techs lose the bell too).
        key: "quiet.toolbar",
        name: "Quiet toolbar",
        roles: ["office", "tech"],
        shipped: "2026-10-10",
        intro: {
            title: "The bell is gone. Look for the mark on the thing.",
            steps: [
                { pic: "quiet1", line: "A colored mark sits on the job, email or task." },
                { pic: "quiet2", line: "Each button counts only its own: Inbox, Tasks, Stuck." },
                { pic: "quiet3", line: "What happened is in Recent activity. Nothing to clear." },
            ],
        },
        usedWhen: "Turned on the quiet toolbar.",
    },
    {
        key: "closeout.notes-shared",
        name: "Shared job notes",
        roles: ["office"],
        shipped: "2026-10-16",
        intro: {
            // Corbin 10/16: notes went private by mistake (#1905); now shared again.
            title: "Notes on jobs are shared with the office again.",
            steps: [
                { pic: "notes1", line: "Tap the star. Write why the job isn't out yet." },
                { pic: "notes2", line: "Everyone in the office sees it, with your name and date." },
                { pic: "notes3", line: "To give someone the job, use Hand to. A note never does." },
            ],
        },
        usedWhen: "Opened the notes on a Closeout job.",
    },
    {
        key: "field-add.ipad",
        name: "Add from the field (iPad)",
        roles: ["tech"],
        shipped: "2026-10-26",
        intro: {
            // Approved mockup DYgYcRWa5K5pw8tBURp7fG (10/26).
            title: "New: the green + on your job",
            steps: [
                { pic: "fa1", line: "Tap + for a photo, a note or a file." },
                { pic: "fa2", line: "Pick where it goes. This job is already picked." },
                { pic: "fa3", line: "The office sees it. The customer never does." },
            ],
        },
        usedWhen: "Saved a photo, note or file with the green +.",
        adminColumn: true,
    },
    {
        key: "field-add.office",
        name: "From the field",
        roles: ["office"],
        shipped: "2026-10-26",
        intro: {
            title: "From the field: what techs add on the job",
            steps: [
                { pic: "fao1", line: "Tech photos and notes show on the device, location and customer pages." },
                { pic: "fao2", line: "Only the ones marked for the office go in this chip." },
                { pic: "fao3", line: "Put somewhere wrong? Move it. None of it goes to the customer." },
            ],
        },
        usedWhen: "Marked a flagged add read, or moved one.",
    },

    // ── Never-used tips (office dots) ────────────────────────────────────
    {
        key: "search",
        name: "⌘K Search",
        roles: ["office"],
        shipped: "2026-07-20",
        tip: { line: "Press ⌘K anywhere. Type a job, customer or action.", pic: "jpCmdk", order: 4 },
        usedWhen: "Opened ⌘K.",
        adminColumn: true,
    },
    {
        key: "tasks.take",
        name: "Take it / Help",
        roles: ["office"],
        shipped: "2026-10-16",
        tip: { line: "Take a task from a teammate, or help them with it.", pic: "tasks", order: 3 },
        usedWhen: "Pressed Take it or Help on the Team tab.",
    },
    {
        key: "job.move-to",
        name: "Move to ▾",
        roles: ["office"],
        shipped: "2026-10-02",
        tip: { line: "Put the job in its next stage without leaving the page.", pic: "jpMove", order: 3 },
        usedWhen: "Pressed Move to on the job page.",
    },
    {
        key: "job.more",
        name: "More for this job",
        roles: ["office"],
        shipped: "2026-10-02",
        tip: { line: "Every action for this job, in one list. Same as ⌘K.", pic: "jpCmdk", order: 5 },
        usedWhen: "Pressed More for this job.",
    },
    {
        key: "job.pins",
        name: "Pins",
        roles: ["office"],
        shipped: "2026-10-02",
        tip: {
            line: "Keep the buttons you use most on top of every job. Pin them in ⌘K.",
            pic: "jpPins",
            order: 6,
            tryIt: "palette",
        },
        usedWhen: "Pinned or unpinned a job-page button.",
    },
    {
        key: "grid.fit-day",
        name: "Fit day",
        roles: ["office"],
        shipped: "2026-09-20",
        tip: { line: "Squeeze the hours that have work into one screen.", pic: "gridFit", order: 7 },
        usedWhen: "Pressed Fit day.",
    },
    {
        key: "grid.views",
        name: "Map and Route planner",
        roles: ["office"],
        shipped: "2026-09-20",
        tip: { line: "See the same day as a map, or plan a tech's route.", pic: "gridViews", order: 8 },
        usedWhen: "Opened the views menu.",
    },
    {
        key: "grid.week",
        name: "Week",
        roles: ["office"],
        shipped: "2026-09-20",
        tip: { line: "See the whole week. Spot the light days.", pic: "gridWeek", order: 9 },
        usedWhen: "Pressed Week.",
    },
];

export const FEATURE_BY_KEY: ReadonlyMap<string, TeachFeature> = new Map(FEATURES.map((f) => [f.key, f]));

export function feature(key: string): TeachFeature | undefined {
    return FEATURE_BY_KEY.get(key);
}
